import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  EventData,
} from 'react-joyride'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  clearLegacyOnboardingStorage,
  defaultProgress,
  ONBOARDING_VERSION,
  readUserOnboarding,
} from './tour.storage'
import { buildOnboardingSteps } from './tour.steps'
import { OnboardingContextValue, OnboardingProgress, OnboardingStep } from './tour.types'
import { OnboardingTourUI } from './tour.ui'
import { OnboardingWelcome } from './tour.welcome'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { updateOnboardingAction } from '../../redux/specification/specificationAction'
import { OnboardingStatus } from '../../types/specification'
import { useAppDispatch } from '../../redux/store'

interface OnboardingProviderProps {
  children: ReactNode
  enabled: boolean
}

type OnboardingPhase = 'idle' | 'welcome' | 'tour'

const TARGET_WAIT_INTERVAL_MS = 200
const TARGET_WAIT_TIMEOUT_MS = 9000
const ON_ROUTE_MISS_MS = 1600
const PERSIST_DEBOUNCE_MS = 350

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

const clampIndex = (value: number, max: number) => {
  if (value < 0) return 0
  if (value >= max) return Math.max(0, max - 1)
  return value
}

const getStepSelector = (step: OnboardingStep) =>
  typeof step.target === 'string' ? step.target : null

const hasTarget = (step: OnboardingStep) => {
  const selector = getStepSelector(step)
  if (!selector) return false
  return Boolean(document.querySelector(selector))
}

export const OnboardingProvider = ({
  children,
  enabled,
}: OnboardingProviderProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const username = useSelector(selectUsername)
  const specifications = useSelector(selectSpecifications)
  const settings = specifications[0]
  const settingsId = settings?._id

  const steps = useMemo(() => buildOnboardingSteps(t), [t, i18n.language])

  const [progress, setProgress] = useState<OnboardingProgress>(defaultProgress)
  const [isHydrated, setIsHydrated] = useState(false)
  const [phase, setPhase] = useState<OnboardingPhase>('idle')
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null)
  const hasAutoStartedRef = useRef(false)
  const persistTimerRef = useRef<number | null>(null)
  const progressRef = useRef(progress)

  progressRef.current = progress

  const persistProgress = useCallback(
    (
      updates: Partial<OnboardingProgress> & { status: OnboardingStatus },
      immediate = false
    ) => {
      const next: OnboardingProgress = {
        ...progressRef.current,
        ...updates,
        version: ONBOARDING_VERSION,
        updatedAt: Date.now(),
      }
      setProgress(next)
      progressRef.current = next

      if (!settingsId) return

      const flush = () => {
        persistTimerRef.current = null
        dispatch(
          updateOnboardingAction({
            id: settingsId,
            status: next.status,
            stepIndex: next.stepIndex,
            version: ONBOARDING_VERSION,
          })
        )
      }

      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }

      if (immediate) {
        flush()
        return
      }

      persistTimerRef.current = window.setTimeout(flush, PERSIST_DEBOUNCE_MS)
    },
    [dispatch, settingsId]
  )

  const findNextStep = useCallback(
    (startIndex: number, direction: 1 | -1) => {
      let cursor = startIndex
      while (cursor >= 0 && cursor < steps.length) {
        const candidate = steps[cursor]
        if (typeof candidate.target === 'string') {
          return cursor
        }
        cursor += direction
      }
      return null
    },
    [steps]
  )

  const finishTour = useCallback(() => {
    setRun(false)
    setPendingStepIndex(null)
    setPhase('idle')
    persistProgress(
      { status: 'completed', stepIndex: Math.max(0, steps.length - 1) },
      true
    )
  }, [persistProgress, steps.length])

  const markTourSkipped = useCallback(() => {
    setRun(false)
    setPendingStepIndex(null)
    setPhase('idle')
    persistProgress({ status: 'skipped', stepIndex: progressRef.current.stepIndex }, true)
  }, [persistProgress])

  const scheduleStep = useCallback(
    (index: number) => {
      if (!steps.length) return

      if (index >= steps.length) {
        finishTour()
        return
      }

      const boundedIndex = clampIndex(index, steps.length)
      const nextStep = steps[boundedIndex]

      persistProgress({
        status: 'in_progress',
        stepIndex: boundedIndex,
      })

      setPhase('tour')
      setRun(false)
      setPendingStepIndex(boundedIndex)

      if (location.pathname !== nextStep.route) {
        navigate(nextStep.route)
      }
    },
    [finishTour, location.pathname, navigate, persistProgress, steps]
  )

  const startTour = useCallback(() => {
    if (!enabled || !steps.length) return
    const targetIndex = clampIndex(progressRef.current.stepIndex, steps.length)
    scheduleStep(targetIndex)
  }, [enabled, scheduleStep, steps.length])

  const restartTour = useCallback(() => {
    setProgress({ ...defaultProgress, status: 'in_progress' })
    progressRef.current = { ...defaultProgress, status: 'in_progress' }
    setStepIndex(0)
    setPendingStepIndex(null)
    setRun(false)
    hasAutoStartedRef.current = true
    setPhase('tour')
    scheduleStep(0)
  }, [scheduleStep])

  const skipTour = useCallback(() => {
    markTourSkipped()
  }, [markTourSkipped])

  const moveStep = useCallback(
    (delta: 1 | -1, currentIndex: number) => {
      const nextIndex = currentIndex + delta
      if (nextIndex >= steps.length) {
        finishTour()
        return
      }
      if (nextIndex < 0) {
        scheduleStep(0)
        return
      }
      scheduleStep(nextIndex)
    },
    [finishTour, scheduleStep, steps.length]
  )

  const handleJoyrideCallback = useCallback((_data: EventData) => {
    // Joyride only draws the overlay/spotlight. Skip, back, and next are owned by
    // the custom tooltip. If we honoured step:after here, stopping the overlay
    // (run=false) would look like "Next" and undo Skip/Back.
  }, [])

  useEffect(() => {
    clearLegacyOnboardingStorage()
  }, [])

  useEffect(() => {
    if (!enabled || !username) {
      setRun(false)
      setPendingStepIndex(null)
      setPhase('idle')
      hasAutoStartedRef.current = false
      setIsHydrated(false)
      return
    }

    const hydrated = readUserOnboarding(settings?.Onboarding, username)
    setProgress(hydrated)
    progressRef.current = hydrated
    setStepIndex(clampIndex(hydrated.stepIndex, Math.max(steps.length, 1)))
    setIsHydrated(true)
    // Hydrate once per workspace/user — local progress is the source of truth during the tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, settingsId, username, steps.length])

  useEffect(() => {
    if (!enabled || !isHydrated || !steps.length) return
    if (progress.status === 'completed' || progress.status === 'skipped') return
    if (hasAutoStartedRef.current) return

    hasAutoStartedRef.current = true
    if (progress.status === 'in_progress') {
      startTour()
      return
    }
    setPhase('welcome')
  }, [enabled, isHydrated, progress.status, startTour, steps.length])

  useEffect(() => {
    if (pendingStepIndex === null || !enabled) return

    const step = steps[pendingStepIndex]
    const stepSelector = step ? getStepSelector(step) : null
    if (!step || !stepSelector) {
      moveStep(1, pendingStepIndex)
      return
    }

    let cancelled = false
    const startedAt = Date.now()

    const intervalId = window.setInterval(() => {
      if (cancelled) return

      if (hasTarget(step)) {
        setStepIndex(pendingStepIndex)
        setPendingStepIndex(null)
        setRun(true)
        window.clearInterval(intervalId)
        return
      }

      if (Date.now() - startedAt >= TARGET_WAIT_TIMEOUT_MS) {
        window.clearInterval(intervalId)
        const fallbackIndex = findNextStep(pendingStepIndex + 1, 1)
        setPendingStepIndex(null)
        if (fallbackIndex === null) {
          finishTour()
          return
        }
        scheduleStep(fallbackIndex)
        return
      }

      const onExpectedRoute = location.pathname === step.route
      if (onExpectedRoute && Date.now() - startedAt >= ON_ROUTE_MISS_MS) {
        window.clearInterval(intervalId)
        const fallbackIndex = findNextStep(pendingStepIndex + 1, 1)
        setPendingStepIndex(null)
        if (fallbackIndex === null) {
          finishTour()
          return
        }
        scheduleStep(fallbackIndex)
      }
    }, TARGET_WAIT_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [enabled, findNextStep, finishTour, location.pathname, moveStep, pendingStepIndex, scheduleStep, steps])

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current)
      }
    }
  }, [])

  const translatedLabels = useMemo(
    () => ({
      back: t('onboarding.actions.back'),
      next: t('onboarding.actions.next'),
      nextWithProgress: t('onboarding.actions.nextWithProgress'),
      skip: t('onboarding.actions.skip'),
      finish: t('onboarding.actions.finish'),
      tryThis: t('onboarding.actions.tryThis'),
    }),
    [t, i18n.language]
  )

  const contextValue = useMemo<OnboardingContextValue>(
    () => ({
      isRunning: run || phase === 'welcome',
      stepIndex,
      hasCompleted: progress.status === 'completed',
      hasSkipped: progress.status === 'skipped',
      startTour,
      restartTour,
      skipTour,
    }),
    [
      phase,
      progress.status,
      restartTour,
      run,
      skipTour,
      startTour,
      stepIndex,
    ]
  )

  const joyrideSteps: OnboardingStep[] = useMemo(() => steps, [steps])

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
      {phase === 'welcome' ? (
        <OnboardingWelcome
          onStart={() => {
            persistProgress({ status: 'in_progress', stepIndex: 0 }, true)
            startTour()
          }}
          onSkip={skipTour}
        />
      ) : null}
      <OnboardingTourUI
        run={run}
        stepIndex={stepIndex}
        steps={joyrideSteps}
        onCallback={handleJoyrideCallback}
        onNext={() => moveStep(1, stepIndex)}
        onBack={() => moveStep(-1, stepIndex)}
        onSkip={skipTour}
        labels={translatedLabels}
      />
    </OnboardingContext.Provider>
  )
}

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboardingContext must be used inside OnboardingProvider')
  }
  return context
}
